import React, {FC, useState, useEffect} from "react"

import {Body, H2} from "@irati/ui/src/Typography"
import FormattedMessage, {InjectIntl, injectIntl} from "@irati/utils/lang"
import {IconTypes} from "@irati/ui/src/Icon"
import {useTheme} from "@irati/ui/src/global/useTheme"
import Flex from "@irati/ui/src/Flex"
import GenericModal from "@irati/ui/src/GenericModal"
import adminReportingService from "@irati/adminReporting/services/adminReporting"
import {
  Deal,
  PerkRedemptionsReport,
  DealStatus,
  BusinessRedemptionReportRedemptionType,
  PaymentCurrencyType,
  ReportingCostOfLiving,
  ReportingOverview,
  DealListSort,
  RedemptionReportDuration
} from "@irati/types"
import dealsService from "@irati/deals/services/dealsGraph"
import intercom from "@irati/utils/intercom"

import {formatCurrency} from "@irati/utils/currency/formatCurrency"
import authentication from "@irati/auth"
import productSettingsService from "@irati/permissions/services/productSettings"
import {adminDashboardTracking} from "@irati-tracking/admin"
import Spacing from "@irati/ui/src/Spacing"
import {RouterLink} from "@irati/ui/src/Link"
import Button from "@irati/ui/src/Button"
import {ALL_TEAMS} from "../../../../Reporting/Region"
import BoldNumberSmallText from "../BoldNumberSmallText"
import ModalWidget from "../ModalWidget"
import Widget from "../Widget"
import IconTextSavings from "../IconTextSavings"
import {FLEXI_PERK, REWARD} from "../"
import TopPerks from "./TopPerks"
import {
  getShareText,
  getEncourageText,
  getButtonText,
  getFlexiPerksTitle,
  getFlexiPerksText,
  getPerksText,
  getPerksTitle,
  getBoldCompaniesText,
  getTopPerksText,
  getTopPerksBodyText,
  getCopyText
} from "./phraseKeys"

interface Props extends InjectIntl {
  costOfLiving: ReportingCostOfLiving
  currencyCode: PaymentCurrencyType
  overview: ReportingOverview
}

const CostOfLivingWidget: FC<Props> = ({costOfLiving, currencyCode, overview, formatMessage}) => {
  const [showNudgeModal, setShowNudgeModal] = useState<boolean>(false)
  const [showPerks, setShowPerks] = useState<boolean>(false)
  const [showNewPerks, setShowNewPerks] = useState<boolean>(false)
  const [teamId, setTeamId] = useState<string>(ALL_TEAMS)
  const [region, setRegion] = useState<string>("")
  const [perks, setPerks] = useState<any[]>([])
  const [hasFlexiPerks, setHasFlexiPerks] = useState<boolean>(false)
  const [hasPerksInReport, setHasPerksInCurrentReport] = useState<boolean>(true)
  const [load, setLoad] = useState<Promise<any> | null>(null)
  const [hasPerksInAnyReport, setHasPerksInAnyReport] = useState<boolean>(true)
  const [regionId, setRegionId] = useState<string>("")

  const theme = useTheme()
  const flexiPointsSavings = (currencyCode === PaymentCurrencyType.Gbp ? costOfLiving?.flexiPointsGbp : costOfLiving?.flexiPointsAud) || 0
  const rewardPointsSavings =
    (currencyCode === PaymentCurrencyType.Gbp ? costOfLiving?.rewardPointsGbp : costOfLiving?.rewardPointsAud) || 0
  const digitalVoucherSavings =
    (currencyCode === PaymentCurrencyType.Gbp ? costOfLiving?.digitalVouchersGbp : costOfLiving?.digitalVouchersAud) || 0
  const discountVoucherSavings =
    (currencyCode === PaymentCurrencyType.Gbp ? costOfLiving?.discountVouchersGbp : costOfLiving?.discountVouchersAud) || 0
  const fixedSavings = flexiPointsSavings + rewardPointsSavings + digitalVoucherSavings
  const totalSavings = fixedSavings + discountVoucherSavings
  const estimatedSavingsIntercomTourId = 482217

  useEffect(() => {
    const {uuid, rootTeamId} = authentication.business()
    const loading = productSettingsService.get(uuid, rootTeamId).then(({perksSettings}) => {
      if (perksSettings?.ttAllowance > 0) {
        setHasFlexiPerks(true)
        return getBusinessRedemptionReport([BusinessRedemptionReportRedemptionType.RedemptionTypeFlexiPoints]).then((initialPerks) => {
          return getInitialPerks(initialPerks, true)
        })
      }
      return getBusinessRedemptionReport().then((initialPerks) => {
        return getInitialPerks(initialPerks, false)
      })
    })

    setLoad(loading)
  }, [])

  useEffect(() => {
    if (showNewPerks) {
      const loading = () => getPerks(DealListSort.New)
      setLoad(loading())
    }
  }, [showNewPerks])

  useEffect(() => {
    const loading = () => {
      if (hasPerksInAnyReport && !showNewPerks) {
        setHasPerksInCurrentReport(true)
        const types = hasFlexiPerks ? [BusinessRedemptionReportRedemptionType.RedemptionTypeFlexiPoints] : undefined
        return getBusinessRedemptionReport(types).then((newPerks) => {
          if (newPerks?.length === 3) {
            return newPerks
          }
          setHasPerksInCurrentReport(false)
          return getPerks(DealListSort.Popularity, hasFlexiPerks)
        })
      }
      const sort = showNewPerks ? DealListSort.New : DealListSort.Popularity
      const ttOnly = showNewPerks ? undefined : hasFlexiPerks
      return getPerks(sort, ttOnly)
    }
    setLoad(loading())
  }, [teamId, showPerks])

  const getInitialPerks = (initialPerks: Deal[], hasFlexi: boolean) => {
    if (initialPerks?.length) {
      return setPerks(initialPerks)
    }
    setHasPerksInAnyReport(false)
    setHasPerksInCurrentReport(false)
    return getPerks(DealListSort.Popularity, hasFlexi)
  }

  const handleUpdateRegion = (updatedTeamId: string, updatedRegion: {label: string; regionId: string}) => {
    setPerks([])
    setTeamId(updatedTeamId)
    setRegionId(updatedRegion?.regionId)
    setRegion(updatedRegion?.label)
  }

  const handleBack = () => {
    setTeamId(ALL_TEAMS)
    setRegion("")
    setRegionId("")
    setShowPerks(false)
    setShowNewPerks(false)
  }

  const handleClick = () => {
    setShowNudgeModal(true)
    adminDashboardTracking.costOfLivingInsights()
  }

  const handleFlexiPerksClick = () => {
    setShowPerks(true)
    adminDashboardTracking.costOfLivingActions("cta_shareFlexiPerks")
  }

  const handlePerksClick = () => {
    setShowPerks(true)
    adminDashboardTracking.costOfLivingActions("cta_sharePerks")
  }

  const handleNewPerks = () => {
    setShowNewPerks(true)
    adminDashboardTracking.costOfLivingActions("cta_shareNewPerks")
  }

  const getPerks = (sort: DealListSort, ttOnly?: boolean) => {
    return dealsService
      .list({
        regions: teamId !== ALL_TEAMS ? [regionId] : undefined,
        sort,
        ttOnly,
        limit: 3
      })
      .then(({results}) => {
        setPerks(results)
        return results
      })
  }

  const getBusinessRedemptionReport = (types?: any) => {
    return adminReportingService
      .getBusinessRedemptionReport(
        {
          businessId: authentication.business().uuid,
          limit: 3,
          duration: RedemptionReportDuration.Last_30Days,
          teams: teamId !== ALL_TEAMS ? [teamId] : undefined,
          types
        },
        {fetchPolicy: "network-only"}
      )
      .then((res) => {
        const businessRedemptionReportPerks = res.report.perks
        const uuids = businessRedemptionReportPerks.map((item: PerkRedemptionsReport) => {
          return item.dealId
        })

        if (uuids.length < 3) {
          setPerks([])
          return []
        }

        return dealsService
          .list(
            {
              uuids,
              ignoreBlacklist: true,
              ignorePackage: true,
              statuses: [DealStatus.On, DealStatus.Off, DealStatus.Unavailable, DealStatus.Hidden, DealStatus.Archived],
              limit: 3
            },
            {fetchPolicy: "network-only"}
          )
          .then((list) => {
            if (list && list.results.length > 0) {
              const topPerks: Deal[] = list.results
                .map((item: Deal) => ({
                  ...item,
                  ...businessRedemptionReportPerks.find((p: PerkRedemptionsReport) => p.uuid === item.uuid)
                }))
                .sort((a, b) => b.redemptions - a.redemptions)
              setPerks(topPerks)
              return topPerks
            }
          })
      })
  }

  const handleStartIntercomTour = () => {
    intercom.startTour(estimatedSavingsIntercomTourId)
  }

  const arePerksInReportFlexi = hasFlexiPerks && hasPerksInReport
  const unspentFlexiPoints =
    currencyCode === PaymentCurrencyType.Gbp ? overview?.unspentFlexiPointsGbp : overview?.unspentFlexiPointsAud || 0
  const encourageText = getEncourageText(unspentFlexiPoints, currencyCode)
  const shareText = getShareText(arePerksInReportFlexi && !showNewPerks)
  const buttonText = getButtonText(unspentFlexiPoints, currencyCode)
  const flexiPerksTitle = getFlexiPerksTitle(hasPerksInReport)
  const flexiPerksText = getFlexiPerksText(hasPerksInReport)
  const perksTitle = getPerksTitle(hasPerksInReport)
  const perksText = getPerksText(hasPerksInReport)
  const bold = getBoldCompaniesText()
  const topPerksText = getTopPerksText(showNewPerks, hasPerksInReport, arePerksInReportFlexi, teamId, region)
  const topPerksBodyText = getTopPerksBodyText(arePerksInReportFlexi, region)
  const copyText = getCopyText(region, teamId, perks, formatMessage, "costOfLivingWidget")

  return (
    <>
      <Widget
        title="admin.components.dashboard.reporting.costOfLiving.title"
        icon={IconTypes.wallet}
        iconColor={theme.colors.tertiary.y_400}
        buttonText={buttonText}
        tooltip="admin.components.dashboard.reporting.costOfLiving.tooltip"
        usePrimaryBtn
        onClick={handleClick}>
        {totalSavings > 0 ? (
          <>
            <Flex marginBottom={1} justifyContent="space-between" alignItems="center">
              <BoldNumberSmallText
                boldValue={formatCurrency({value: totalSavings, currencyCode, isCents: false})}
                text="admin.components.dashboard.reporting.costOfLiving.total"
              />
              <Button small cta id="costOfLivingAdminTourBtn" onClick={handleStartIntercomTour}>
                <FormattedMessage id="admin.components.dashboard.reporting.costOfLiving.whatIsThis" />
              </Button>
            </Flex>
            <Spacing marginLeft>
              <Flex marginBottom={1} justifyContent="space-between" alignItems="center">
                <BoldNumberSmallText
                  boldValue={formatCurrency({value: fixedSavings, currencyCode, isCents: false})}
                  text="admin.components.dashboard.reporting.costOfLiving.fixedSavings"
                  small
                />
                <RouterLink to="analytics/perks" cta small>
                  {formatMessage({id: "admin.components.dashboard.reporting.costOfLiving.viewReport"})}
                </RouterLink>
              </Flex>
              <Spacing marginLeft>
                {!!rewardPointsSavings && (
                  <IconTextSavings
                    type={REWARD}
                    text="admin.components.dashboard.reporting.costOfLiving.rewardPointsSavings"
                    amount={rewardPointsSavings}
                    currencyCode={currencyCode}
                    marginBottom={1}
                  />
                )}
                {!!flexiPointsSavings && (
                  <IconTextSavings
                    type={FLEXI_PERK}
                    text="admin.components.dashboard.reporting.costOfLiving.flexiPointsSavings"
                    amount={flexiPointsSavings}
                    currencyCode={currencyCode}
                    marginBottom={1}
                  />
                )}
                <IconTextSavings
                  text="admin.components.dashboard.reporting.costOfLiving.giftCardSavings"
                  amount={digitalVoucherSavings}
                  currencyCode={currencyCode}
                  marginBottom={1}
                />
              </Spacing>
              <Spacing marginBottom={1}>
                <BoldNumberSmallText
                  boldValue={formatCurrency({value: discountVoucherSavings, currencyCode, isCents: false})}
                  text="admin.components.dashboard.reporting.costOfLiving.estimatedSavings"
                  small
                />
              </Spacing>
              <Spacing marginLeft>
                <IconTextSavings
                  text="admin.components.dashboard.reporting.costOfLiving.discountVouchersSavings"
                  amount={discountVoucherSavings}
                  currencyCode={currencyCode}
                  marginBottom={3}
                />
              </Spacing>
            </Spacing>
          </>
        ) : (
          <>
            <Flex justifyContent="center" marginBottom={1}>
              <img width="100px" src={formatMessage({id: "admin.components.dashboard.reporting.costOfLiving.noSavings.img"})} />
            </Flex>
            <Body small>
              <FormattedMessage id="admin.components.dashboard.reporting.costOfLiving.noSavings.text" />
            </Body>
          </>
        )}
      </Widget>
      <GenericModal
        show={showNudgeModal}
        hideActions
        customWidth={884}
        onHide={() => {
          handleBack()
          setShowNudgeModal(false)
        }}>
        <H2 marginBottom={1}>
          <FormattedMessage id="admin.components.dashboard.reporting.costOfLiving.modal.title" />
        </H2>
        {(showPerks || showNewPerks) && (
          <TopPerks
            onUpdateRegion={handleUpdateRegion}
            onBack={handleBack}
            perks={perks}
            load={load}
            regionId={teamId}
            topPerksText={topPerksText}
            topPerksBodyText={topPerksBodyText}
            copyText={copyText}
            shareText={shareText}
          />
        )}
        {!showPerks && !showNewPerks && (
          <>
            {!!unspentFlexiPoints && (
              <Body marginBottom={2} maxWidth="100%">
                <FormattedMessage id={encourageText} />
              </Body>
            )}
            <Flex gap={2}>
              {hasFlexiPerks ? (
                <>
                  <ModalWidget
                    title={flexiPerksTitle}
                    buttonText="admin.components.dashboard.reporting.costOfLiving.modal.widgets.flexiPerks.button"
                    emoji="📢"
                    onClick={handleFlexiPerksClick}
                    text={<FormattedMessage id={flexiPerksText} values={{bold}} />}
                    isRecommended
                  />
                  <ModalWidget
                    title="admin.components.dashboard.reporting.costOfLiving.modal.widgets.newPerks.title"
                    buttonText="admin.components.dashboard.reporting.costOfLiving.modal.widgets.newPerks.button"
                    emoji="🆕"
                    onClick={handleNewPerks}
                    text={
                      <FormattedMessage
                        id="admin.components.dashboard.reporting.costOfLiving.modal.widgets.newPerks.text"
                        values={{bold}}
                      />
                    }
                  />
                </>
              ) : (
                <>
                  <ModalWidget
                    title="admin.components.dashboard.reporting.costOfLiving.modal.widgets.buyFP.title"
                    buttonText="admin.components.dashboard.reporting.costOfLiving.modal.widgets.buyFP.button"
                    emoji="🆙"
                    onClick={() => intercom.addCustomMessage("")}
                    text="admin.components.dashboard.reporting.costOfLiving.modal.widgets.buyFP.text"
                    isRecommended
                  />
                  <ModalWidget
                    title={perksTitle}
                    buttonText="admin.components.dashboard.reporting.costOfLiving.modal.widgets.perks.button"
                    emoji="📢"
                    onClick={handlePerksClick}
                    text={<FormattedMessage id={perksText} values={{bold}} />}
                  />
                </>
              )}
              <ModalWidget
                title="admin.components.dashboard.reporting.costOfLiving.modal.widgets.marketing.title"
                buttonText="admin.components.dashboard.reporting.costOfLiving.modal.widgets.marketing.button"
                href="admin.components.dashboardTile.resourceHub.link"
                emoji="🙌"
                text="admin.components.dashboard.reporting.costOfLiving.modal.widgets.marketing.text"
                onClick={() => adminDashboardTracking.costOfLivingActions("cta_marketingResource")}
              />
            </Flex>
          </>
        )}
      </GenericModal>
    </>
  )
}

export default injectIntl(CostOfLivingWidget)
